import { useEffect, useState, type ComponentType, type SVGProps } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import type { Capability } from '@pos/core';
import { formatDeviceCode } from '@pos/core';
import { knownBusinesses, requestPersistentStorage } from '../lib/db.ts';
import { BusinessSwitcherButton } from '../features/auth/BusinessSwitcher.tsx';
import { useOnline } from '../lib/use-online.ts';
import { useServerUserId } from '../lib/server-session.ts';
import { startBackgroundSync } from '../services/sync.ts';
import { logout } from '../services/session.ts';
import { StalePendingBanner, SyncBadge } from '../features/sync/SyncBadge.tsx';
import { LicenseBanner, LicenseBlock, useLicenseBlocked } from '../features/license/LicenseNotices.tsx';
import { ForcePasswordChange } from '../features/auth/ChangePasswordDialog.tsx';
import { startLicenseRefresh } from '../services/license.ts';
import { Avatar, Button, Spinner, cx } from '../ui/index.tsx';
import { AppearanceMenu } from '../ui/ThemeToggle.tsx';
import { BoxIcon, CartIcon, CashIcon, ChartIcon, ClockIcon, HomeIcon, LogoutIcon, MoreIcon, ReceiptIcon, SettingsIcon, TagIcon, UsersIcon } from '../ui/icons.tsx';
import { BusinessLogo } from './BusinessLogo.tsx';
import { useSession } from './current.tsx';

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  capability?: Capability;
  ownerOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Inicio', icon: HomeIcon, ownerOnly: true },
  { to: '/vender', label: 'Vender', icon: CartIcon, capability: 'sell' },
  { to: '/ventas', label: 'Ventas', icon: ReceiptIcon, capability: 'sell' },
  { to: '/caja', label: 'Caja', icon: CashIcon, capability: 'operate_own_cash' },
  { to: '/clientes', label: 'Clientes', icon: UsersIcon, capability: 'sell' },
  { to: '/productos', label: 'Productos', icon: TagIcon, capability: 'view_stock' },
  { to: '/stock', label: 'Stock', icon: BoxIcon, capability: 'view_stock' },
  { to: '/reportes', label: 'Reportes', icon: ChartIcon, capability: 'view_reports' },
  { to: '/actividad', label: 'Actividad', icon: ClockIcon, capability: 'view_reports' },
  { to: '/configuracion', label: 'Configuración', icon: SettingsIcon, capability: 'manage_settings' },
];

/**
 * Quien entró sin conexión no tiene sesión en el servidor: sus cambios quedan en el dispositivo
 * y las acciones que requieren servidor (crear empleados, cambiar PIN) no funcionan hasta
 * que vuelva a entrar con internet.
 */
function OfflineSessionNote() {
  const online = useOnline();
  return (
    <p className="mt-2 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-900 ring-1 ring-amber-200">
      Entraste sin conexión.
      {online && ' Ya hay internet: tocá "Cambiar de usuario" y volvé a entrar para sincronizar.'}
    </p>
  );
}

/**
 * Entró con conexión, pero la sesión con el servidor se cerró (venció y no se pudo renovar): los
 * cambios se guardan en el dispositivo pero no se envían, y lo que se consulta al servidor
 * (reportes, otros dispositivos) falla. Hay que volver a entrar.
 */
function LostSessionBanner({ onReenter, leaving }: { onReenter: () => void; leaving: boolean }) {
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200">
      <span>Se cerró tu sesión con el servidor: lo que cargues se guarda en este dispositivo, pero no se envía ni se ven los datos de los otros dispositivos.</span>
      <Button size="sm" loading={leaving} onClick={onReenter}>
        Volver a entrar
      </Button>
    </div>
  );
}

const BOTTOM_MAX = 5;

/** Navegación inferior (celulares): hasta 5 botones; si hay más, los últimos van a "Más". */
function BottomNav({ items }: { items: NavItem[] }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMoreOpen(false), [location.pathname]);
  const overflow = items.length > BOTTOM_MAX;
  const visible = overflow ? items.slice(0, BOTTOM_MAX - 1) : items;
  const hidden = overflow ? items.slice(BOTTOM_MAX - 1) : [];
  const hiddenActive = hidden.some((i) => location.pathname.startsWith(i.to));
  const cell = (active: boolean) =>
    cx('flex flex-col items-center gap-0.5 pt-2 pb-1.5 text-[11px] font-medium', active ? 'text-slate-900' : 'text-slate-500');
  // El ícono de la sección actual va en una pastilla del color del negocio.
  const pill = (active: boolean) =>
    cx('flex h-7 w-12 items-center justify-center rounded-full transition-colors', active && 'bg-brand text-brand-fg shadow-card');

  return (
    <nav className="pb-safe fixed inset-x-0 bottom-0 z-10 border-t border-slate-200 bg-white md:hidden">
      {moreOpen && (
        <div className="absolute inset-x-2 bottom-full mb-2 rounded-xl bg-white p-1 shadow-raised ring-1 ring-slate-200">
          {hidden.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cx('flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium', isActive ? 'bg-brand text-brand-fg' : 'text-slate-700')
              }
            >
              <item.icon className="size-5" />
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
      <div className="grid" style={{ gridTemplateColumns: `repeat(${visible.length + (overflow ? 1 : 0)}, minmax(0, 1fr))` }}>
        {visible.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.to === '/'} className={({ isActive }) => cell(isActive)}>
            {({ isActive }) => (
              <>
                <span className={pill(isActive)}>
                  <item.icon className="size-5" />
                </span>
                {item.label}
              </>
            )}
          </NavLink>
        ))}
        {overflow && (
          <button type="button" className={cell(hiddenActive || moreOpen)} onClick={() => setMoreOpen((o) => !o)} aria-expanded={moreOpen}>
            <span className={pill(hiddenActive || moreOpen)}>
              <MoreIcon className="size-5" />
            </span>
            Más
          </button>
        )}
      </div>
    </nav>
  );
}

// Botones de la barra lateral (abajo): mismo aspecto que el de «Apariencia».
const sideButton =
  'flex w-full items-center gap-3 rounded-lg p-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50';

export function AppLayout() {
  const { business, membership, device, session, can, isOwner } = useSession();
  const [leaving, setLeaving] = useState(false);

  useEffect(() => startBackgroundSync(), []);
  useEffect(() => startLicenseRefresh(device.id), [device.id]);
  const blocked = useLicenseBlocked();
  const online = useOnline();
  const serverUser = useServerUserId();
  // Sesión del servidor perdida (o de otro usuario) estando con conexión.
  const lostServerSession = online && session.mode === 'online' && serverUser !== undefined && serverUser !== membership.user_id;
  useEffect(() => {
    void requestPersistentStorage();
  }, []);

  const items = NAV.filter((i) => (i.ownerOnly ? isOwner : i.capability ? can(i.capability) : true));

  const switchUser = async () => {
    setLeaving(true);
    try {
      await logout();
    } finally {
      setLeaving(false);
    }
  };

  return (
    <div className="flex min-h-full">
      {/* Barra lateral (tablet horizontal y PC): oscura también en modo claro y fija al bajar. */}
      <aside className="surface-dark sticky top-0 hidden h-dvh w-60 shrink-0 flex-col border-r border-slate-200 bg-white text-slate-900 md:flex">
        <div className="flex items-center gap-3 px-4 pt-5 pb-3">
          <BusinessLogo name={business.name} logoPath={business.logo_path} className="size-10 rounded-xl text-lg shadow-card" />
          <div className="min-w-0">
            <p className="truncate font-semibold tracking-tight">{business.name}</p>
            <p className="truncate text-xs text-slate-500">
              {device.name} · {formatDeviceCode(device.code)}
            </p>
          </div>
        </div>
        <div className="px-4 pb-2">
          <SyncBadge />
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-2">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cx(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive ? 'bg-brand text-brand-fg shadow-card' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                )
              }
            >
              <item.icon className="size-5 shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-3">
          <div className="flex items-center gap-3 px-1 pb-2">
            <Avatar name={membership.display_name} />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{membership.display_name}</p>
              <p className="text-xs text-slate-500">{isOwner ? 'Dueño' : 'Empleado'}</p>
            </div>
          </div>
          {session.mode === 'offline' && <OfflineSessionNote />}
          <AppearanceMenu withLabel up className="mt-1" />
          <button type="button" className={sideButton} onClick={switchUser} disabled={leaving}>
            {leaving ? <Spinner className="size-5" /> : <LogoutIcon className="size-5" />}
            Cambiar de usuario
          </button>
          {knownBusinesses().length > 1 && <BusinessSwitcherButton variant="button" className="justify-start" />}
        </div>
      </aside>

      <div className="app-canvas flex min-w-0 flex-1 flex-col">
        {/* Celular y tablet vertical: barra de arriba (en PC todo esto está en la barra lateral). */}
        <header className="pt-safe sticky top-0 z-20 border-b border-slate-200 bg-white md:hidden">
          <div className="flex h-14 items-center gap-2 px-3">
            <BusinessLogo name={business.name} logoPath={business.logo_path} className="size-8 rounded-lg text-sm" />
            <p className="min-w-0 flex-1 truncate font-semibold tracking-tight">{business.name}</p>
            <SyncBadge />
            <AppearanceMenu />
            <button
              type="button"
              onClick={switchUser}
              disabled={leaving}
              className="rounded-lg p-2 text-slate-600 hover:bg-slate-100"
              aria-label="Cambiar de usuario"
            >
              {leaving ? <Spinner className="size-5" /> : <LogoutIcon className="size-5" />}
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-x-clip px-3 pt-4 pb-24 sm:px-5 md:pb-6 lg:px-6 lg:pt-5">
          {session.mode === 'offline' && (
            <div className="mb-4 md:hidden">
              <OfflineSessionNote />
            </div>
          )}
          {lostServerSession && <LostSessionBanner onReenter={switchUser} leaving={leaving} />}
          <LicenseBanner />
          <StalePendingBanner />
          {blocked ? <LicenseBlock /> : <Outlet />}
          {isOwner && <ForcePasswordChange />}
        </main>

        <BottomNav items={items} />
      </div>
    </div>
  );
}
